import { useEffect, useState } from 'react';

import { duracion } from '@haus/core';

import { Boton } from '../componentes/Boton.js';
import { Hoja, estilosHoja } from '../componentes/Hoja.js';
import { Aviso, BarraAccion, Cabecera, Chip, Contenido, Pantalla } from '../componentes/estructura.js';
import {
    guardarLimpieza,
    leerLimpieza,
    nombreEstancia,
    type EstanciaLocal,
    type LimpiezaLocal,
} from '../datos/db.js';
import { marcarEstancia, siguienteEstancia } from '../datos/limpiezas.js';
import { useApp } from '../estado/app.js';
import css from './comun.module.css';

/**
 * 04 · Recorrido por estancias.
 *
 * El mapa de la limpieza: qué queda, qué está hecho y qué no se pudo hacer.
 * Todo el estado sale de IndexedDB, así que esta pantalla funciona igual con
 * el móvil en modo avión.
 *
 * "No accesible" con motivo obligatorio no es burocracia: es la única forma de
 * que una estancia sin vídeo signifique algo. Sin motivo, una limpieza con
 * cuatro estancias grabadas de seis es indistinguible de un trabajo a medias.
 */
export function PantallaRecorrido({ limpiezaId }: { limpiezaId: string }) {
    const { t, idioma, ir, atras, sello } = useApp();
    const [l, setL] = useState<LimpiezaLocal | null>(null);
    const [noAccesible, setNoAccesible] = useState<EstanciaLocal | null>(null);
    const [motivo, setMotivo] = useState('');

    // `sello` cambia con cada navegación: al volver de grabar una estancia hay
    // que releer, porque React no remonta esta pantalla por volver a ella.
    useEffect(() => {
        void leerLimpieza(limpiezaId).then(setL);
    }, [limpiezaId, sello]);

    if (l === null) {
        return (
            <Pantalla>
                <Cabecera titulo={t('comun.cargando')} alVolver={atras} />
            </Pantalla>
        );
    }

    const nombre = (e: EstanciaLocal): string => nombreEstancia(e, idioma);
    const siguiente = siguienteEstancia(l);
    const ordenadas = [...l.estancias].sort((a, b) => a.orden - b.orden);
    const yaHayCaptura = l.estancias.some((e) => e.estado === 'hecha');

    async function cambiarModo(modo: 'video' | 'fotos'): Promise<void> {
        if (l === null || l.modo === modo || yaHayCaptura) {
            return;
        }
        // Solo mientras no se haya grabado nada: cambiar el modo a mitad
        // dejaría una limpieza con tres clips y dos checklists, que no es ni
        // una cosa ni la otra.
        const nueva = { ...l, modo };
        await guardarLimpieza(nueva);
        setL(nueva);
    }

    function abrirEstancia(e: EstanciaLocal): void {
        if (l === null) {
            return;
        }
        ir(
            l.modo === 'video'
                ? { n: 'grabar', limpieza: l.id, estancia: e.id }
                : { n: 'fotos', limpieza: l.id, estancia: e.id }
        );
    }

    return (
        <Pantalla>
            <Cabecera
                titulo={l.apartamento}
                subtitulo={t(l.modo === 'video' ? 'recorrido.subtitulo_video' : 'recorrido.subtitulo_fotos', {
                    n: l.estancias.length,
                })}
                alVolver={atras}
            />

            <Contenido>
                {/* Un apartamento sin estancias es un fallo de configuración
                    de oficina, y sin avisarlo la empleada se queda mirando una
                    lista vacía sin nada que pulsar salvo "Ir al resumen", que
                    cerraría la limpieza sin una sola prueba. */}
                {l.estancias.length === 0 && (
                    <Aviso>
                        <div>
                            <strong>{t('recorrido.sin_estancias')}</strong>
                            <div style={{ fontWeight: 400, marginTop: 'var(--e4)' }}>
                                {t('recorrido.sin_estancias_ayuda')}
                            </div>
                        </div>
                    </Aviso>
                )}

                <div className={css.modos}>
                    {(['video', 'fotos'] as const).map((m) => (
                        <button
                            key={m}
                            className={`${css.modo} ${l.modo === m ? css.modoActivo : ''}`}
                            disabled={yaHayCaptura}
                            onClick={() => void cambiarModo(m)}
                        >
                            <span className={css.modoTitulo}>
                                {t(m === 'video' ? 'recorrido.modo_video' : 'recorrido.modo_fotos')}
                            </span>
                            <span className={css.modoSub}>
                                {t(m === 'video' ? 'recorrido.modo_video_sub' : 'recorrido.modo_fotos_sub')}
                            </span>
                        </button>
                    ))}
                </div>

                <div className={css.grupo}>
                    {ordenadas.map((e) => {
                        const actual = siguiente?.id === e.id;
                        const clase =
                            e.estado === 'pendiente' && !actual
                                ? css.estanciaPendiente
                                : actual
                                  ? css.estanciaActual
                                  : '';

                        return (
                            <button
                                key={e.id}
                                className={`${css.fila} ${clase}`}
                                onClick={() => (e.estado === 'pendiente' ? abrirEstancia(e) : undefined)}
                            >
                                <span
                                    className={`${css.circulo} ${
                                        e.estado === 'hecha'
                                            ? css.circuloOk
                                            : e.estado === 'no_accesible'
                                              ? css.circuloWarn
                                              : actual
                                                ? css.circuloAccent
                                                : ''
                                    }`}
                                >
                                    {e.estado === 'hecha' ? '✓' : e.estado === 'no_accesible' ? '!' : e.orden}
                                </span>
                                <div className={css.filaTextos}>
                                    <span className={css.filaTitulo}>{nombre(e)}</span>
                                    {e.estado === 'no_accesible' && (
                                        <span className={css.filaSub}>{e.motivo_no_accesible}</span>
                                    )}
                                </div>
                                {e.estado === 'hecha' && (
                                    <span className="mono">
                                        {e.video_ms !== null ? duracion(e.video_ms) : `${e.fotos} fotos`}
                                    </span>
                                )}
                                {actual && <Chip tono="accent">{t('recorrido.siguiente_etiqueta')}</Chip>}
                            </button>
                        );
                    })}
                </div>

                {siguiente !== null && (
                    <Boton variante="secundario" onClick={() => setNoAccesible(siguiente)}>
                        {t('recorrido.no_accesible')}
                    </Boton>
                )}
            </Contenido>

            <BarraAccion>
                {siguiente !== null ? (
                    <Boton onClick={() => abrirEstancia(siguiente)}>
                        {t(l.modo === 'video' ? 'recorrido.grabar' : 'recorrido.fotografiar', {
                            estancia: nombre(siguiente),
                        })}
                    </Boton>
                ) : (
                    <Boton onClick={() => ir({ n: 'resumen', limpieza: l.id })}>
                        {t('recorrido.terminar')}
                    </Boton>
                )}
            </BarraAccion>

            {noAccesible !== null && (
                <Hoja
                    titulo={t('recorrido.no_accesible_titulo')}
                    ayuda={t('recorrido.no_accesible_ayuda')}
                    alCerrar={() => {
                        setNoAccesible(null);
                        setMotivo('');
                    }}
                >
                    <textarea
                        className={estilosHoja.campo}
                        placeholder={t('recorrido.no_accesible_ejemplo')}
                        value={motivo}
                        maxLength={300}
                        onChange={(e) => setMotivo(e.target.value)}
                    />
                    <Boton
                        disabled={motivo.trim().length < 3}
                        onClick={() => {
                            void (async () => {
                                const nueva = await marcarEstancia(l.id, noAccesible.id, {
                                    estado: 'no_accesible',
                                    motivo_no_accesible: motivo.trim(),
                                });
                                setL(nueva);
                                setNoAccesible(null);
                                setMotivo('');
                            })();
                        }}
                    >
                        {t('comun.aceptar')}
                    </Boton>
                </Hoja>
            )}
        </Pantalla>
    );
}
